import React, { useEffect, useState, Fragment } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ROWS_PER_PAGE = 34;

const CommodityWise = () => {
  const [groupedData, setGroupedData] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // ✅ Added contract date filters
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: ''
  });

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Format number in Indian style
  const formatIndianCurrency = (num) => {
    if (!num || isNaN(num)) return '0.00';
    const numStr = num.toFixed(2);
    const [integerPart, decimalPart] = numStr.split('.');
    const reversed = integerPart.split('').reverse();
    let formatted = '';
    
    for (let i = 0; i < reversed.length; i++) {
      if (i === 3) {
        formatted = ',' + formatted;
      } else if (i > 3 && (i - 3) % 2 === 0) {
        formatted = ',' + formatted;
      }
      formatted = reversed[i] + formatted;
    }
    
    return `${formatted}.${decimalPart}`;
  };

  // Group data by commodity and calculate subtotals
  const groupByCommodity = (data) => {
    const groups = {};
    data.forEach(row => {
      if (!groups[row.commodity_id]) {
        groups[row.commodity_id] = {
          commodity_id: row.commodity_id,
          commodity: row.commodity,
          rows: [],
          subtotalQty: 0,
          subtotalImportQty: 0,
          subtotalAmount: 0,
          subtotalAmountInr: 0
        };
      }
      groups[row.commodity_id].rows.push(row);
      groups[row.commodity_id].subtotalQty += parseFloat(row.contracted_quantity_mt) || 0;
      groups[row.commodity_id].subtotalImportQty += parseFloat(row.import_order_qty) || 0;
      groups[row.commodity_id].subtotalAmount += parseFloat(row.contract_amount) || 0;
      groups[row.commodity_id].subtotalAmountInr += parseFloat(row.amount_in_inr) || 0;
    });
    return Object.values(groups);
  };

  // Function to truncate commodity name slightly
  const truncateCommodityName = (text) => {
    if (!text) return '-';
    return text.length > 18 ? text.substring(0, 18) + '...' : text;
  };

  // ✅ Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // ✅ Reset filters function
  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: ''
    });
    setPage(1);
  };

  // Debounce search input
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim(),
        contractDateFrom: filters.contractDateFrom,
        contractDateTo: filters.contractDateTo
      });

      const res = await fetch(`${API_BASE_URL}/api/report/commodity-wise-report?${params.toString()}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();

      setGroupedData(groupByCommodity(result.data || []));
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load report. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Updated useEffect to include filters
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [debouncedSearch, page, filters]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  // Calculate statistics
  const totalContracts = groupedData.reduce((sum, group) => sum + group.rows.length, 0);
  const totalCommodities = groupedData.length;
  const totalValueUSD = groupedData.reduce((sum, group) => sum + group.subtotalAmount, 0);
  const totalValueINR = groupedData.reduce((sum, group) => sum + group.subtotalAmountInr, 0);
  const totalQuantity = groupedData.reduce((sum, group) => sum + group.subtotalQty, 0);

  return (
    <div className="max-w-8xl mx-auto">
      <div className="bg-white border border-gray-200 p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Commodity Wise Report</h2>
            <p className="text-sm text-gray-500 mt-1">Comprehensive analysis of commodity transactions grouped by product categories</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="Search by contract, commodity, country..."
              value={search}
              onChange={handleSearchChange}
              className="border border-gray-300 px-3 py-2 focus:outline-none w-full sm:w-56 text-sm"
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm"
            >
              <FaFilter />
              {showFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* ✅ Filter Section */}
        {showFilters && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col w-48">
                <label className="text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
                <input
                  type="date"
                  name="contractDateFrom"
                  value={filters.contractDateFrom}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
                />
              </div>

              <div className="flex flex-col w-48">
                <label className="text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
                <input
                  type="date"
                  name="contractDateTo"
                  value={filters.contractDateTo}
                  onChange={handleFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
                />
              </div>

              <div className="flex gap-2 mt-1">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Commodities</div>
              <div className="text-xl font-bold text-gray-800">{totalCommodities}</div>
            </div>
            <div className="bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Contracts</div>
              <div className="text-xl font-bold text-gray-800">{totalContracts}</div>
            </div>
            <div className="bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Total Quantity</div>
              <div className="text-xl font-bold text-gray-800">{formatIndianCurrency(totalQuantity).split('.')[0]} MT</div>
            </div>
            <div className="bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Value (INR)</div>
              <div className="text-xl font-bold text-gray-800">₹{formatIndianCurrency(totalValueINR).split('.')[0]}</div>
            </div>
            <div className="bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Value (USD)</div>
              <div className="text-xl font-bold text-gray-800">${formatIndianCurrency(totalValueUSD).split('.')[0]}</div>
            </div>
          </div>
        )}

        {/* Table & States */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-gray-600"></div>
            <p className="text-sm text-gray-600">Loading commodity data...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 p-4 mb-4">
            <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        ) : groupedData.length === 0 ? (
          <NoRecordFound message="No commodity reports found" />
        ) : (
          <div className="overflow-x-auto border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700 font-semibold">
                <tr>
                  <th className="px-4 py-2 text-left">Contract No</th>
                  <th className="px-4 py-2 text-left">Contract Date</th>
                  <th className="px-4 py-2 text-left w-32">Commodity</th>
                  <th className="px-4 py-2 text-left">Country</th>
                  <th className="px-4 py-2 text-left">Import Type</th>
                  <th className="px-4 py-2 text-right">Contract Qty (MT)</th>
                  <th className="px-4 py-2 text-right">Import Qty (MT)</th>
                  <th className="px-4 py-2 text-right">Price</th>
                  <th className="px-4 py-2 text-left">Currency</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Ex. Rate</th>
                  <th className="px-4 py-2 text-right">Amount (INR)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {groupedData.map((group, groupIdx) => (
                  <Fragment key={groupIdx}>
                    {group.rows.map((row, idx) => (
                      <tr key={`${groupIdx}-${idx}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium">{row.purchase_contract_no}</td>
                        <td className="px-4 py-3">{formatDate(row.contract_date)}</td>
                        {idx === 0 && (
                          <td 
                            className="px-4 py-3 text-sm text-gray-900 font-semibold bg-gray-50 w-32" 
                            rowSpan={group.rows.length}
                            title={group.commodity}
                          >
                            {truncateCommodityName(group.commodity)}
                          </td>
                        )}
                        <td className="px-4 py-3">{row.country}</td>
                        <td className="px-4 py-3">{row.import_type}</td>
                        <td className="px-4 py-3 text-right">{formatIndianCurrency(parseFloat(row.contracted_quantity_mt) || 0)}</td>
                        <td className="px-4 py-3 text-right">{formatIndianCurrency(parseFloat(row.import_order_qty) || 0)}</td>
                        <td className="px-4 py-3 text-right">{formatIndianCurrency(parseFloat(row.price_usd) || 0)}</td>
                        <td className="px-4 py-3">{row.currency || 'USD'}</td>
                        <td className="px-4 py-3 text-right">{formatIndianCurrency(parseFloat(row.contract_amount) || 0)}</td>
                        <td className="px-4 py-3 text-right">{formatIndianCurrency(parseFloat(row.exchange_rate) || 0)}</td>
                        <td className="px-4 py-3 text-right font-bold">₹{formatIndianCurrency(parseFloat(row.amount_in_inr) || 0)}</td>
                      </tr>
                    ))}
                    {/* Enhanced Subtotal row */}
                    <tr className="bg-gradient-to-r from-blue-100 to-indigo-100 border-t-2 border-blue-200">
                      <td colSpan="5" className="px-4 py-3 text-right text-sm font-bold text-blue-800 uppercase tracking-wide">
                        Subtotal for {truncateCommodityName(group.commodity)}:
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-blue-900 bg-blue-50">{formatIndianCurrency(group.subtotalQty)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-blue-900 bg-blue-50">{formatIndianCurrency(group.subtotalImportQty)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">-</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">-</td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-blue-900 bg-blue-50">{formatIndianCurrency(group.subtotalAmount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">-</td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-blue-900 bg-blue-50">₹{formatIndianCurrency(group.subtotalAmountInr)}</td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center gap-2 mt-6 text-sm">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`px-3 py-2 border ${page === 1 ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
            >
              Prev
            </button>
            <span className="text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={`px-3 py-2 border ${page === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommodityWise;
